import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Calculator, 
  Clock, 
  Shield, 
  CheckCircle2, 
  ArrowRight, 
  Phone, 
  Mail, 
  MapPin,
  Menu,
  X,
  Banknote,
  Users,
  FileText,
  Upload,
  File,
  Percent,
  Square,
  CheckSquare
} from 'lucide-react';

import { User } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LandingPage() {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [applicationOpen, setApplicationOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(null);
  const [loanAmount, setLoanAmount] = useState(2000);
  const [preScreen, setPreScreen] = useState({
    income: false,
    employment: false,
    sa_id: false,
    bank_account: false,
    no_debt_review: false,
  });
  const allChecked = Object.values(preScreen).every(Boolean);
  const [loanFees, setLoanFees] = useState({
    principal: 2000,
    initiation_fee: 250,
    interest: 100,
    interest_rate: '5%',
    admin_fee: 60,
    insurance: 60,
    insurance_rate: '3%',
    total_fees: 470,
    total_repayment: 2470,
  });
  const [formData, setFormData] = useState({
    full_name: '',
    id_number: '',
    email: '',
    phone: '',
    address: '',
    employer_name: '',
    monthly_income: '',
    bank_name: '',
    account_number: '',
    id_document_url: null,
    payslip_url: null,
    bank_statement_url: null,
  });
  const [uploadedFiles, setUploadedFiles] = useState({
    id_document: null,
    payslip: null,
    bank_statement: null
  });
  const [applyLink, setApplyLink] = useState('');

  const debounceRef = useRef(null);

  const fetchFees = useCallback(async (amount) => {
    try {
      const res = await axios.get(`${API_URL}/calculator`, { params: { principal: amount } });
      setLoanFees(res.data);
    } catch {
      // Fallback calculation client-side
      const interest = amount * 0.05;
      const initiation_fee = amount <= 1000 ? amount * 0.15 : 1000 * 0.15 + (amount - 1000) * 0.10;
      const admin_fee = 60;
      const insurance = amount * 0.03;
      const total_fees = initiation_fee + interest + admin_fee + insurance;
      setLoanFees({
        principal: amount,
        initiation_fee,
        interest,
        interest_rate: '5%',
        admin_fee,
        insurance,
        insurance_rate: '3%',
        total_fees,
        total_repayment: amount + total_fees,
      });
    }
  }, []);

  useEffect(() => {
    fetchFees(2000);
    axios.post(`${API_URL}/track/visit`).catch(() => {});
    axios.get(`${API_URL}/site-settings`).then(r => setApplyLink(r.data.apply_link || '')).catch(() => {});
  }, [fetchFees]);

  const handleSliderChange = (e) => {
    const val = parseInt(e.target.value, 10);
    setLoanAmount(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchFees(val), 150);
  };

  const fmt = (n) => `R ${Number(n).toLocaleString('en-ZA', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  const fmtDec = (n) => `R ${Number(n).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileUpload = async (e, fileType) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a JPG, PNG or PDF file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum 5MB allowed');
      return;
    }

    setUploadingFile(fileType);

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API_URL}/upload/document`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const fieldMap = {
        id_document: 'id_document_url',
        payslip: 'payslip_url',
        bank_statement: 'bank_statement_url'
      };

      setFormData(prev => ({
        ...prev,
        [fieldMap[fileType]]: response.data.url
      }));

      setUploadedFiles(prev => ({
        ...prev,
        [fileType]: file.name
      }));

      toast.success(`${file.name} uploaded successfully`);
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploadingFile(null);
    }
  };

  const handleSubmitApplication = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const payload = {
        ...formData,
        monthly_income: parseFloat(formData.monthly_income),
        loan_amount: loanAmount,
        loan_term_days: 30
      };

      await axios.post(`${API_URL}/loans/apply`, payload);
      toast.success('Application submitted successfully! We will contact you shortly.');
      setApplicationOpen(false);
      setFormData({
        full_name: '',
        id_number: '',
        email: '',
        phone: '',
        address: '',
        employer_name: '',
        monthly_income: '',
        bank_name: '',
        account_number: '',
        id_document_url: null,
        payslip_url: null,
        bank_statement_url: null,
      });
      setUploadedFiles({
        id_document: null,
        payslip: null,
        bank_statement: null
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setIsSubmitting(false);
    }
  };

  const FileUploadField = ({ label, fileType, required = false }) => (
    <div className="space-y-2">
      <Label className="flex items-center gap-2">
        <File className="w-4 h-4" />
        {label} {required && '*'}
      </Label>
      <div className="relative">
        <input
          type="file"
          accept=".jpg,.jpeg,.png,.pdf"
          onChange={(e) => handleFileUpload(e, fileType)}
          className="hidden"
          id={`file-${fileType}`}
          data-testid={`input-${fileType}`}
        />
        <label
          htmlFor={`file-${fileType}`}
          className={`flex items-center justify-center gap-2 h-12 px-4 rounded-xl border-2 border-dashed cursor-pointer transition-colors ${
            uploadedFiles[fileType] 
              ? 'border-emerald-300 bg-emerald-50 text-emerald-700' 
              : 'border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/50'
          }`}
        >
          {uploadingFile === fileType ? (
            <div className="spinner"></div>
          ) : uploadedFiles[fileType] ? (
            <>
              <CheckCircle2 className="w-4 h-4" />
              <span className="text-sm truncate max-w-[200px]">{uploadedFiles[fileType]}</span>
            </>
          ) : (
            <>
              <Upload className="w-4 h-4" />
              <span className="text-sm">Upload {label}</span>
            </>
          )}
        </label>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-emerald-900 rounded-xl flex items-center justify-center">
                <Banknote className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-emerald-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>
                2k2day
              </span>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="#calculator" className="nav-link text-slate-600 hover:text-emerald-900 font-medium">Calculator</a>
              <a href="#how-it-works" className="nav-link text-slate-600 hover:text-emerald-900 font-medium">How It Works</a>
              <a href="#contact" className="nav-link text-slate-600 hover:text-emerald-900 font-medium">Contact</a>
              <Link to="/client/login">
                <Button variant="outline" size="sm" className="border-emerald-900 text-emerald-900 hover:bg-emerald-50 rounded-full" data-testid="nav-login-btn">
                  <User className="w-4 h-4 mr-1.5" />Login
                </Button>
              </Link>
            </div>

            {/* Mobile menu button */}
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-slate-100 py-4 px-4 space-y-3">
            <a href="#calculator" className="block py-2 text-slate-600">Calculator</a>
            <a href="#how-it-works" className="block py-2 text-slate-600">How It Works</a>
            <a href="#contact" className="block py-2 text-slate-600">Contact</a>
            <Link to="/client/login" className="block py-2 text-emerald-900 font-medium" data-testid="mobile-login-link">
              Login / Register
            </Link>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="hero-gradient pt-24 pb-16 md:pt-32 md:pb-24 relative overflow-hidden">
        <div className="absolute inset-0 noise-overlay"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Content */}
            <div className="lg:col-span-6 space-y-8 animate-fade-in-up">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-emerald-100 text-emerald-800 text-sm font-medium">
                <Percent className="w-4 h-4 mr-2" />
                Low Interest - Only 5%
              </div>
              
              <h1 className="text-5xl md:text-7xl font-bold text-emerald-900 tracking-tighter leading-tight" style={{ fontFamily: 'Manrope' }}>
                Get up to R2,000<br />
                <span className="text-emerald-500">Today</span>
              </h1>
              
              <p className="text-lg md:text-xl text-slate-600 leading-relaxed max-w-lg">
                Fast micro loans for South Africans. Simple application, <strong>low 5% interest fee</strong>, 
                money in your account within 24 hours.
              </p>

              {/* Pre-Screening Checklist */}
              <div className="bg-white/80 backdrop-blur rounded-2xl p-5 border border-slate-200 space-y-3" data-testid="pre-screen-checklist">
                <p className="text-sm font-semibold text-emerald-900 uppercase tracking-wider">Do you qualify?</p>
                {[
                  { key: 'income', label: 'Income R8,000+ gross per month' },
                  { key: 'employment', label: '6 months continuous employment' },
                  { key: 'sa_id', label: 'Valid South African ID' },
                  { key: 'bank_account', label: 'Bank account in your own name' },
                  { key: 'no_debt_review', label: 'Not under debt review or judgments' },
                ].map(({ key, label }) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setPreScreen(prev => ({ ...prev, [key]: !prev[key] }))}
                    className={`flex items-center gap-3 w-full text-left py-1.5 px-2 rounded-lg transition-colors ${
                      preScreen[key] ? 'text-emerald-800' : 'text-slate-500 hover:bg-slate-50'
                    }`}
                    data-testid={`prescreen-${key}`}
                  >
                    {preScreen[key] ? (
                      <CheckSquare className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                    ) : (
                      <Square className="w-5 h-5 text-slate-300 flex-shrink-0" />
                    )}
                    <span className="text-sm font-medium">{label}</span>
                  </button>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                {allChecked ? (
                  <Button 
                    className="h-14 px-10 rounded-full bg-emerald-900 hover:bg-emerald-800 text-lg shadow-lg hover:shadow-emerald-900/20 animate-fade-in-up"
                    data-testid="apply-now-btn"
                    onClick={() => {
                      axios.post(`${API_URL}/track/apply-click`).catch(() => {});
                      navigate('/apply');
                    }}
                  >
                    Apply Now
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                ) : (
                  <div className="h-14 px-10 rounded-full bg-slate-200 text-slate-400 text-lg flex items-center justify-center cursor-not-allowed" data-testid="apply-now-btn-disabled">
                    Complete checklist to apply
                  </div>
                )}

                <a href="#calculator">
                  <Button 
                    variant="outline" 
                    className="h-14 px-10 rounded-full border-emerald-200 hover:bg-emerald-50 text-emerald-900 text-lg"
                    data-testid="calculate-btn"
                  >
                    <Calculator className="mr-2 w-5 h-5" />
                    Calculate
                  </Button>
                </a>
              </div>
            </div>

            {/* Right - Calculator Card */}
            <div className="lg:col-span-6 animate-slide-in-right stagger-2">
              <div id="calculator" className="calculator-card rounded-3xl p-8 relative">
                <div className="absolute top-4 right-4">
                  <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                    <Calculator className="w-6 h-6 text-emerald-600" />
                  </div>
                </div>

                <div className="inline-flex items-center px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-medium mb-4">
                  <Percent className="w-3 h-3 mr-1" />
                  Low 5% Interest
                </div>

                <h3 className="text-xl font-semibold text-slate-800 mb-6" style={{ fontFamily: 'Manrope' }}>
                  Loan Calculator
                </h3>

                <div className="space-y-6">
                  <div className="bg-slate-50 rounded-2xl p-6">
                    <p className="text-sm text-slate-500 uppercase tracking-wider mb-2">Loan Amount</p>
                    <p className="text-5xl font-bold text-emerald-900 tabular-nums" data-testid="loan-amount">
                      {fmt(loanAmount)}
                    </p>
                    <div className="mt-4 space-y-2">
                      <input
                        type="range"
                        min={200}
                        max={2000}
                        step={100}
                        value={loanAmount}
                        onChange={handleSliderChange}
                        className="loan-slider w-full"
                        data-testid="loan-slider"
                      />
                      <div className="flex justify-between text-xs text-slate-400 font-medium">
                        <span>R200</span>
                        <span>R2,000</span>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-slate-50 rounded-xl p-4">
                      <p className="text-sm text-slate-500 mb-1">Initiation Fee</p>
                      <p className="text-xl font-bold text-slate-700 tabular-nums" data-testid="initiation-fee">
                        {fmt(loanFees.initiation_fee)}
                      </p>
                    </div>
                    <div className="bg-slate-50 rounded-xl p-4">
                      <p className="text-sm text-slate-500 mb-1">Admin Fee</p>
                      <p className="text-xl font-bold text-slate-700 tabular-nums" data-testid="admin-fee">
                        {fmt(loanFees.admin_fee)}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-emerald-50 rounded-xl p-4">
                      <p className="text-sm text-emerald-600 mb-1">Interest (5%)</p>
                      <p className="text-xl font-bold text-emerald-700 tabular-nums" data-testid="interest-amount">
                        {fmt(loanFees.interest)}
                      </p>
                    </div>
                    <div className="bg-blue-50 rounded-xl p-4">
                      <p className="text-sm text-blue-600 mb-1">Insurance (3%)</p>
                      <p className="text-xl font-bold text-blue-700 tabular-nums" data-testid="insurance-amount">
                        {fmt(loanFees.insurance)}
                      </p>
                    </div>
                  </div>

                  <div className="bg-amber-50 rounded-xl p-3">
                    <div className="flex justify-between items-center">
                      <p className="text-sm text-amber-600 font-medium">Total Fees</p>
                      <p className="text-xl font-bold text-amber-700 tabular-nums" data-testid="total-fees">
                        {fmt(loanFees.total_fees)}
                      </p>
                    </div>
                  </div>

                  <div className="bg-emerald-900 rounded-xl p-4 text-white">
                    <p className="text-sm text-emerald-200 mb-1">Total Repayment</p>
                    <p className="text-3xl font-bold tabular-nums" data-testid="total-amount">
                      {fmt(loanFees.total_repayment)}
                    </p>
                    <p className="text-xs text-emerald-300 mt-1">Over 30 days</p>
                  </div>

                  <div className="flex items-center justify-between text-sm text-slate-500 pt-4 border-t border-slate-100">
                    <span className="flex items-center">
                      <Clock className="w-4 h-4 mr-1" />
                      Term: 1 Month
                    </span>
                    <span className="flex items-center">
                      <Shield className="w-4 h-4 mr-1" />
                      No Hidden Fees
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-emerald-900 tracking-tight mb-4" style={{ fontFamily: 'Manrope' }}>
              How It Works
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Get your loan in three simple steps. Quick, transparent, and hassle-free.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: FileText,
                title: 'Apply Online',
                description: 'Fill out our simple application form and upload your ID, payslip, and bank statement.',
                step: '01'
              },
              {
                icon: Users,
                title: 'Quick Review',
                description: 'Our team reviews your application and documents, getting back to you within hours.',
                step: '02'
              },
              {
                icon: Banknote,
                title: 'Get Your Money',
                description: 'Once approved, receive R2,000 directly in your bank account.',
                step: '03'
              }
            ].map((item, index) => (
              <Card 
                key={index} 
                className="card-hover border-slate-100 overflow-hidden"
                data-testid={`step-card-${index + 1}`}
              >
                <CardHeader className="pb-4">
                  <div className="flex items-start justify-between">
                    <div className="w-14 h-14 rounded-2xl bg-emerald-100 flex items-center justify-center">
                      <item.icon className="w-7 h-7 text-emerald-600" />
                    </div>
                    <span className="text-5xl font-bold text-slate-100" style={{ fontFamily: 'Manrope' }}>
                      {item.step}
                    </span>
                  </div>
                </CardHeader>
                <CardContent>
                  <CardTitle className="text-xl mb-3 text-emerald-900">{item.title}</CardTitle>
                  <p className="text-slate-600 leading-relaxed">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Fee Breakdown Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-bold text-emerald-900 tracking-tight mb-4" style={{ fontFamily: 'Manrope' }}>
              Transparent Pricing
            </h2>
            <p className="text-lg text-slate-600">
              No hidden fees. See exactly what you'll pay.
            </p>
          </div>

          <Card className="border-slate-200 shadow-lg">
            <CardContent className="p-8">
              <div className="space-y-4">
                <div className="flex justify-between items-center py-3 border-b border-slate-100">
                  <span className="text-lg text-slate-600">Loan Amount</span>
                  <span className="text-2xl font-bold text-emerald-900 tabular-nums" data-testid="pricing-loan-amount">{fmt(loanFees.principal)}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-slate-100">
                  <span className="text-lg text-slate-600">Initiation Fee</span>
                  <span className="text-xl font-semibold text-slate-700 tabular-nums" data-testid="pricing-initiation-fee">{fmt(loanFees.initiation_fee)}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-slate-100">
                  <div className="flex items-center gap-2">
                    <span className="text-lg text-slate-600">Interest</span>
                    <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full">5%/month</span>
                  </div>
                  <span className="text-xl font-semibold text-emerald-600 tabular-nums" data-testid="pricing-interest">{fmt(loanFees.interest)}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-slate-100">
                  <span className="text-lg text-slate-600">Admin Fee</span>
                  <span className="text-xl font-semibold text-slate-700 tabular-nums" data-testid="pricing-admin-fee">{fmt(loanFees.admin_fee)}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-slate-100">
                  <div className="flex items-center gap-2">
                    <span className="text-lg text-slate-600">Insurance</span>
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">3%/month</span>
                  </div>
                  <span className="text-xl font-semibold text-blue-600 tabular-nums" data-testid="pricing-insurance">{fmt(loanFees.insurance)}</span>
                </div>
                <div className="flex justify-between items-center py-3 bg-amber-50 -mx-4 px-4 rounded-lg">
                  <span className="text-lg font-semibold text-amber-700">Total Fees</span>
                  <span className="text-xl font-bold text-amber-700 tabular-nums" data-testid="pricing-total-fees">{fmt(loanFees.total_fees)}</span>
                </div>
                <div className="flex justify-between items-center py-4 bg-emerald-900 -mx-4 px-4 rounded-lg mt-4">
                  <span className="text-xl font-semibold text-white">Total Repayment</span>
                  <span className="text-3xl font-bold text-white tabular-nums" data-testid="pricing-total-repayment">{fmt(loanFees.total_repayment)}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-20 bg-emerald-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 noise-overlay opacity-5"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-3xl md:text-5xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>
                Trusted by South Africans
              </h2>
              <p className="text-emerald-200 text-lg leading-relaxed">
                We understand that unexpected expenses happen. That's why we've made getting 
                a micro loan simple, fast, and transparent. No surprises, no hidden fees.
              </p>
              <ul className="space-y-4">
                {[
                  'Low 5% monthly interest',
                  'All fees transparently shown upfront',
                  'Fast approval - usually within 24 hours',
                  'Simple repayment over 30 days'
                ].map((item, index) => (
                  <li key={index} className="flex items-center">
                    <CheckCircle2 className="w-5 h-5 text-emerald-400 mr-3 flex-shrink-0" />
                    <span className="text-emerald-100">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1579940906483-0ea99f5d04bb?crop=entropy&cs=srgb&fm=jpg&q=85&w=600"
                alt="South African Rand notes"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-6 shadow-xl">
                <p className="text-4xl font-bold text-emerald-900 tabular-nums">R2,000</p>
                <p className="text-slate-600">Available Today</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-bold text-emerald-900 tracking-tight mb-4" style={{ fontFamily: 'Manrope' }}>
              Get In Touch
            </h2>
            <p className="text-lg text-slate-600">
              Have questions? We're here to help.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              {
                icon: Phone,
                title: 'Phone',
                value: '0800 2K2 DAY',
                subtext: 'Mon-Fri, 8am-5pm'
              },
              {
                icon: Mail,
                title: 'Email',
                value: 'info@2k2day.co.za',
                subtext: 'We reply within 24 hours'
              },
              {
                icon: MapPin,
                title: 'Office',
                value: 'Johannesburg',
                subtext: 'South Africa'
              }
            ].map((item, index) => (
              <div 
                key={index} 
                className="text-center p-6 rounded-2xl bg-white border border-slate-100 card-hover"
                data-testid={`contact-card-${index + 1}`}
              >
                <div className="w-14 h-14 rounded-2xl bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-7 h-7 text-emerald-600" />
                </div>
                <h3 className="font-semibold text-emerald-900 mb-2">{item.title}</h3>
                <p className="text-lg font-medium text-slate-800">{item.value}</p>
                <p className="text-sm text-slate-500">{item.subtext}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-emerald-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="flex items-center space-x-2 mb-4 md:mb-0">
              <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center">
                <Banknote className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>
                2k2day
              </span>
            </div>
            <div className="flex items-center space-x-6 text-emerald-300 text-sm">
              <a href="https://secure.quinnpay.com" target="_blank" rel="noopener noreferrer" className="hover:text-white" data-testid="admin-link">Admin</a>
              <Link to="/admin/login" className="hover:text-white" data-testid="dashboard-link">Dashboard</Link>
              <span>© 2025 2k2day. All rights reserved.</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
